import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

const isSignType = (type) => type === 'signature' || type === 'initials';

/**
 * One signing field drawn over a PDF page. Positions are percentages of the
 * page, converted from PDF points (origin bottom-left).
 */
const FieldOverlay = ({ field, page, value, adopted, highlighted, onActivate }) => {
  const { t } = useTranslation();
  if (field.type === 'envelope_id') return null;

  const style = {
    position: 'absolute',
    left: `${(field.x / page.width) * 100}%`,
    top: `${((page.height - field.y - field.height) / page.height) * 100}%`,
    width: `${(field.width / page.width) * 100}%`,
    height: `${(field.height / page.height) * 100}%`,
  };

  const filled = isSignType(field.type) ? !!value : field.type === 'checkbox' ? value === 'true' : !!value;
  const needsAction = field.required && !filled;
  const anchorKey = String(field.anchor || '').replace(/\//g, '');
  const fieldLabel = anchorKey
    ? t(`esign.sign.fieldLabels.${anchorKey}`, { defaultValue: field.label })
    : field.label;
  const label = field.type === 'signature'
    ? t('esign.sign.tapToSign')
    : field.type === 'initials'
      ? t('esign.sign.tapToInitial')
      : fieldLabel;

  if (field.type === 'date_signed') {
    return (
      <Box
        id={`esign-field-${field.id}`}
        title={t('esign.sign.dateAuto')}
        sx={{
          ...style,
          bgcolor: 'rgba(25, 118, 210, 0.08)',
          border: '1px dashed rgba(25, 118, 210, 0.5)',
          color: 'rgba(25, 118, 210, 0.9)',
          fontSize: 'clamp(6px, 1.3vw, 11px)',
          display: 'flex',
          alignItems: 'center',
          px: 0.5,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        {t('esign.sign.dateField')}
      </Box>
    );
  }

  const image = field.type === 'signature' ? adopted?.signatureImage : adopted?.initialsImage;

  return (
    <Box
      id={`esign-field-${field.id}`}
      component="button"
      type="button"
      onClick={() => onActivate(field)}
      aria-label={`${label}${field.required ? '' : ` (${t('esign.sign.optional')})`}`}
      aria-pressed={field.type === 'checkbox' ? value === 'true' : undefined}
      sx={{
        ...style,
        p: 0,
        m: 0,
        cursor: 'pointer',
        font: 'inherit',
        border: '1.5px solid',
        borderColor: highlighted ? '#e65100' : needsAction ? '#f9a825' : filled ? 'transparent' : 'rgba(0,0,0,0.25)',
        borderStyle: filled ? 'solid' : 'dashed',
        borderRadius: '3px',
        bgcolor: filled ? 'transparent' : needsAction ? 'rgba(255, 213, 79, 0.75)' : 'rgba(255, 236, 179, 0.6)',
        boxShadow: highlighted ? '0 0 0 3px rgba(230, 81, 0, 0.35)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: field.type === 'checkbox' ? 'center' : 'flex-start',
        overflow: 'visible',
        // A larger, invisible touch target than the drawn box.
        '&::after': { content: '""', position: 'absolute', inset: '-8px' },
        '&:focus-visible': { outline: '2px solid #1565c0', outlineOffset: 2 },
        animation: highlighted && !filled ? 'esignPulse 1.2s ease-in-out 2' : 'none',
        '@keyframes esignPulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.06)' },
        },
      }}
    >
      {isSignType(field.type) && filled && image && (
        <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center bottom' }} />
      )}
      {isSignType(field.type) && !filled && (
        <Box component="span" sx={{ px: 0.5, fontSize: 'clamp(7px, 1.5vw, 12px)', fontWeight: 700, color: '#5d4037', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {label}
          {!field.required && ` · ${t('esign.sign.optional')}`}
        </Box>
      )}
      {field.type === 'checkbox' && value === 'true' && (
        <Box component="span" sx={{ fontSize: 'clamp(8px, 1.6vw, 13px)', lineHeight: 1, color: '#0b1a5a', fontWeight: 700 }}>✓</Box>
      )}
      {['text', 'email', 'number'].includes(field.type) && (
        <Box component="span" sx={{ px: 0.5, fontSize: 'clamp(6px, 1.3vw, 11px)', color: value ? '#0b1a5a' : '#6d4c41', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || `${fieldLabel}${field.required ? '' : ` (${t('esign.sign.optional')})`}`}
        </Box>
      )}
    </Box>
  );
};

export default FieldOverlay;
